import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface Message {
  id: string;
  from?: string;
  to?: string;
  role: string;
  subject: string;
  message: string;
  time: string;
  read?: boolean;
  avatar: string;
}

interface Contact {
  id: string;
  name: string;
  role: string;
  avatar: string;
}

const ParentMessages = () => {
  const routes = all_routes;
  const [activeTab, setActiveTab] = useState("inbox");
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

  // Mock messages data
  const messagesData = {
    inbox: [
      {
        id: "1",
        from: "Ms. Sarah Johnson",
        role: "Math Teacher",
        subject: "Emma's Progress Update",
        message:
          "Emma has shown excellent improvement in algebra. She's now scoring consistently above 85%.",
        time: "2 hours ago",
        read: false,
        avatar: "S",
      },
      {
        id: "2",
        from: "Mr. David Wilson",
        role: "Class Teacher",
        subject: "Parent-Teacher Meeting",
        message:
          "Reminder about the upcoming parent-teacher meeting scheduled for Friday at 3:00 PM.",
        time: "1 day ago",
        read: true,
        avatar: "D",
      },
      {
        id: "3",
        from: "Administration Office",
        role: "Admin",
        subject: "Fee Payment Due",
        message:
          "This is a reminder that the quarterly fee payment is due by the end of this week.",
        time: "3 days ago",
        read: true,
        avatar: "A",
      },
    ],
    sent: [
      {
        id: "4",
        to: "Ms. Sarah Johnson",
        role: "Math Teacher",
        subject: "Homework Help Request",
        message:
          "Could you please provide some additional resources for Emma to practice algebra?",
        time: "5 days ago",
        avatar: "S",
      },
    ],
  };

  const contacts: Contact[] = [
    { id: "1", name: "Ms. Sarah Johnson", role: "Math Teacher", avatar: "S" },
    { id: "2", name: "Mr. David Wilson", role: "Class Teacher", avatar: "D" },
    { id: "3", name: "Administration Office", role: "Admin", avatar: "A" },
    { id: "4", name: "Ms. Emily Brown", role: "Science Teacher", avatar: "E" },
  ];

  const handleMessageClick = (message: Message) => {
    setSelectedMessage(message);
  };

  const handleComposeSubmit = () => {
    setShowComposeModal(false);
    // TODO: Send message API call
  };

  const renderMessageList = (messages: Message[]) => (
    <div className="message-list">
      {messages.length > 0 ? (
        messages.map((message) => (
          <div
            key={message.id}
            className={`message-item p-3 border-bottom cursor-pointer ${
              !message.read ? "bg-light" : ""
            }`}
            onClick={() => handleMessageClick(message)}
          >
            <div className="d-flex align-items-start">
              <div className="avatar avatar-sm bg-primary me-3">
                <span className="text-white">{message.avatar}</span>
              </div>
              <div className="flex-fill">
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <h6 className="mb-0">{message.from || message.to}</h6>
                  <small className="text-muted">{message.time}</small>
                </div>
                <p className="text-muted mb-1 small">{message.role}</p>
                <h6 className={`mb-1 ${!message.read ? "fw-bold" : ""}`}>
                  {message.subject}
                </h6>
                <p className="text-muted mb-0 small">
                  {message.message.substring(0, 100)}...
                </p>
              </div>
              {!message.read && <span className="badge bg-primary">New</span>}
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-5">
          <div className="avatar avatar-lg bg-light mb-3">
            <i className="ti ti-message text-muted fs-2"></i>
          </div>
          <h5>No messages</h5>
          <p className="text-muted">No messages in this category.</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Messages</h3>
              <p className="text-muted mb-0">
                Communicate with teachers and administration
              </p>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-primary"
                onClick={() => setShowComposeModal(true)}
              >
                <i className="ti ti-plus me-2"></i>
                Compose
              </button>
            </div>
          </div>

          {/* Message Tabs */}
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "inbox" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("inbox")}
                  >
                    <i className="ti ti-inbox me-2"></i>
                    Inbox ({messagesData.inbox.filter((m) => !m.read).length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "sent" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("sent")}
                  >
                    <i className="ti ti-send me-2"></i>
                    Sent ({messagesData.sent.length})
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body p-0">
              {activeTab === "inbox" && renderMessageList(messagesData.inbox)}
              {activeTab === "sent" && renderMessageList(messagesData.sent)}
            </div>
          </div>
        </div>
      </div>

      {/* Compose Modal */}
      {showComposeModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Compose Message</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowComposeModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label className="form-label">To:</label>
                    <select className="form-select">
                      <option value="">Select recipient...</option>
                      {contacts.map((contact) => (
                        <option key={contact.id} value={contact.id}>
                          {contact.name} - {contact.role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Subject:</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter subject..."
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Message:</label>
                    <textarea
                      className="form-control"
                      rows={6}
                      placeholder="Type your message here..."
                    ></textarea>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowComposeModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleComposeSubmit}
                >
                  <i className="ti ti-send me-2"></i>
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedMessage.subject}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedMessage(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="d-flex align-items-center mb-3">
                  <div className="avatar avatar-sm bg-primary me-3">
                    <span className="text-white">{selectedMessage.avatar}</span>
                  </div>
                  <div>
                    <h6 className="mb-0">
                      {selectedMessage.from || selectedMessage.to}
                    </h6>
                    <small className="text-muted">
                      {selectedMessage.role} • {selectedMessage.time}
                    </small>
                  </div>
                </div>
                <div className="message-content">
                  <p>{selectedMessage.message}</p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setSelectedMessage(null);
                    setShowComposeModal(true);
                  }}
                >
                  <i className="ti ti-reply me-2"></i>
                  Reply
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedMessage(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ParentMessages;
